import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import useTheme from '@material-ui/core/styles/useTheme'
import CachedIcon from '@material-ui/icons/Cached'
import {
  Typography,
  Menu,
  MenuItem,
  Grid,
  CircularProgress,
} from '@material-ui/core'
import { getCartMiniAction } from '@src/utils/cartUtils'
import { usePatchCartItem } from '@src/utils/useCart'
import { useGetSubscriptionFrequenciesQuery } from '@src/queries/SubscriptionFrequencyQuery.generated'
import subscriptionFrequencyMessages from '@src/utils/MessageSets/defineSubscriptionFrequencyMessages'
import { useIntl } from 'react-intl'
import { CartType } from '@src/routes'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  subscriptionFrequencyContainer: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  subscriptionIcon: {
    fontSize: theme.typography.pxToRem(20),
    marginRight: theme.spacing(1),
  },
  frequencyLink: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
  frequencyMenu: {
    '& .MuiMenu-list': {
      padding: 0,
    },
    '& .MuiMenuItem-root': {
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
}))

interface SplitFrequencyLabel {
  prefix: string
  value: string
}

interface SubscriptionFrequencyProps {
  cartItemId: string | undefined
  cartType: CartType | undefined
  subscriptionFrequency: string | undefined
  isCheckout?: boolean
  styles?: any
}

const SubscriptionFrequency: React.FC<SubscriptionFrequencyProps> = ({
  cartItemId,
  cartType,
  isCheckout,
  subscriptionFrequency,
  styles,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const { formatMessage } = useIntl()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [showLoader, setShowLoader] = useState(false)
  const [currentFrequency, setCurrentFrequency] = useState(
    subscriptionFrequency
  )

  const [patchCartItem, { loading: patchLoading }] = usePatchCartItem()
  const { data: frequencyData } = useGetSubscriptionFrequenciesQuery({
    skip: !subscriptionFrequency,
    ssr: false,
  })

  const formatFrequencyLabel = (code: string) => {
    const frequencyMessage = subscriptionFrequencyMessages[code]
    return frequencyMessage ? formatMessage(frequencyMessage) : code
  }

  const frequencyOptions =
    frequencyData?.subscriptionFrequencies?.map((freq) => ({
      label: formatFrequencyLabel(freq),
      value: freq,
    })) ?? []

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = async (frequency: string) => {
    try {
      setCurrentFrequency(frequency)
      handleClose()
      await patchCartItem({
        variables: {
          input: {
            cartItemId: cartItemId ?? '',
            subscriptionFrequency: frequency,
          },
          action: getCartMiniAction(cartType),
        },
      })
    } catch (error) {
      console.error('Failed to update subscription frequency:', error)
    }
  }

  const splitFrequencyLabel = (message: string): SplitFrequencyLabel => {
    const parts = message.split(/(?=\d)/)
    return {
      prefix: parts[0].trim(),
      value: parts[1]?.trim() ?? '',
    }
  }

  const currentFrequencyLabel = useMemo(() => {
    if (!currentFrequency) return ''
    const message = formatFrequencyLabel(currentFrequency)

    if (isCheckout) {
      return <span>{message}</span>
    }

    const { prefix, value } = splitFrequencyLabel(message)
    return (
      <span>
        {prefix} <span className={classes.frequencyLink}>{value}</span>
      </span>
    )
  }, [
    currentFrequency,
    formatFrequencyLabel,
    theme.palette.primary.main,
    isCheckout,
  ])

  useEffect(() => {
    setShowLoader(patchLoading)
  }, [patchLoading])

  return (
    <Grid
      className={clsx(classes.subscriptionFrequencyContainer, {
        [styles]: styles,
      })}
    >
      {showLoader ? (
        <Grid className={classes.loader}>
          <CircularProgress size={20} />
        </Grid>
      ) : (
        <>
          <CachedIcon
            className={classes.subscriptionIcon}
            htmlColor={theme.palette.primary.main}
          />
          {isCheckout ? (
            <Typography variant="body2">{currentFrequencyLabel}</Typography>
          ) : (
            <>
              <Grid
                onClick={handleClick}
                onKeyDown={() => handleClick}
                style={{ cursor: 'pointer' }}
                aria-controls="frequency-menu"
                aria-haspopup="true"
                role="button"
                tabIndex={0}
              >
                <Typography variant="body2">{currentFrequencyLabel}</Typography>
              </Grid>

              <Menu
                id="frequency-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                className={classes.frequencyMenu}
              >
                {frequencyOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleMenuItemClick(option.value)}
                    selected={currentFrequency === option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </>
      )}
    </Grid>
  )
}

export default SubscriptionFrequency
